import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { StatusRowStack } from '@/components/hangar-view/expanding-card/StatusRowStack'
import type { TodayBoardItem } from './useTodayBoardData'

interface AttentionCardProps {
  item: TodayBoardItem
}

function getRelationshipBadge(data: TodayBoardItem['data']) {
  if (data.leaseContract) {
    return <Badge variant="outline" className="text-[10px] h-5">Lease</Badge>
  }
  if (data.transientStay) {
    return <Badge variant="outline" className="text-[10px] h-5">Transient</Badge>
  }
  return <Badge variant="destructive" className="text-[10px] h-5">No Contract</Badge>
}

function getClientName(data: TodayBoardItem['data']): string | null {
  if (data.leaseContract?.clientName) return data.leaseContract.clientName
  if (data.transientStay?.clientName) return data.transientStay.clientName
  if (data.aircraft.owner?.companyName) return data.aircraft.owner.companyName
  if (data.aircraft.owner?.firstName) {
    return [data.aircraft.owner.firstName, data.aircraft.owner.lastName].filter(Boolean).join(' ')
  }
  if (data.aircraft.ownerName) return data.aircraft.ownerName
  return null
}

export function AttentionCard({ item }: AttentionCardProps) {
  const { data } = item
  const clientName = getClientName(data)
  const locationParts = [data.allocation.areaName, data.allocation.unitCode].filter(Boolean)
  const locationText = locationParts.length > 0 ? locationParts.join(' · ') : null

  return (
    <Card className="overflow-hidden">
      {/* Two-line card header */}
      <div className="px-3 py-2 border-b bg-muted/30 space-y-0.5">
        {/* Line 1: Tail + type + client */}
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono font-semibold text-sm shrink-0">{data.aircraft.tailNumber}</span>
          {data.aircraft.typeCode && (
            <span className="text-xs text-muted-foreground shrink-0">{data.aircraft.typeCode}</span>
          )}
          {clientName && (
            <>
              <span className="text-muted-foreground shrink-0">·</span>
              <span className="text-xs text-muted-foreground truncate">{clientName}</span>
            </>
          )}
        </div>
        {/* Line 2: Location + relationship badge */}
        <div className="flex items-center gap-2 min-w-0">
          {locationText && (
            <span className="text-[10px] text-muted-foreground">{locationText}</span>
          )}
          <div className="ml-auto shrink-0">
            {getRelationshipBadge(data)}
          </div>
        </div>
      </div>

      {/* Status rows — only warning/critical in attention context */}
      <StatusRowStack data={data} filterSeverities={['critical', 'warning']} />
    </Card>
  )
}
